import moment from 'moment'
import { useI18n } from 'vue-i18n'

/**
 * Composable for formatting values (replaces Vue 2 filters)
 */
export function useFormatters() {
  const { t } = useI18n()

  /**
   * Show plural label if time is plural number
   */
  function pluralize(time: number, label: string): string {
    if (time === 1) {
      return time + label
    }
    return time + label + 's'
  }

  /**
   * Format time as "X minutes/hours/days ago"
   */
  function timeAgo(time: number): string {
    const between = Date.now() / 1000 - Number(time)
    if (between < 3600) {
      return pluralize(~~(between / 60), ' minute')
    } else if (between < 86400) {
      return pluralize(~~(between / 3600), ' hour')
    } else {
      return pluralize(~~(between / 86400), ' day')
    }
  }

  /**
   * Number formatting like 10000 => 10k
   */
  function numberFormatter(num: number, digits: number = 2): string {
    const si = [
      { value: 1E18, symbol: 'E' },
      { value: 1E15, symbol: 'P' },
      { value: 1E12, symbol: 'T' },
      { value: 1E9, symbol: 'G' },
      { value: 1E6, symbol: 'M' },
      { value: 1E3, symbol: 'k' }
    ]
    for (let i = 0; i < si.length; i++) {
      if (num >= si[i].value) {
        return (num / si[i].value + 0.1).toFixed(digits).replace(/\.0+$|(\.[0-9]*[1-9])0+$/, '$1') + si[i].symbol
      }
    }
    return num.toString()
  }

  /**
   * 10000 => "10,000"
   */
  function toThousandFilter(num: number): string {
    return (+num || 0).toString().replace(/^-?\d+/g, m => m.replace(/(?=(?!\b)(\d{3})+$)/g, ','))
  }

  /**
   * Upper case first char
   */
  function uppercaseFirst(str: string): string {
    if (!str) return ''
    return str.charAt(0).toUpperCase() + str.slice(1)
  }

  /**
   * Replace commas with dots (for decimal numbers)
   */
  function decimalComma(str: string): string {
    if (!str) return ''
    return str.replace(/,/g, '.')
  }

  /**
   * Format date as DD.MM.YYYY
   */
  function formatDate(value: string | Date | number | null | undefined): string {
    if (!value) return ''
    return moment(value).format('DD.MM.YYYY')
  }

  /**
   * Format Unix timestamp to datetime
   */
  function timestampToDateTime(value: number | null | undefined): string {
    if (!value) return ''
    const date = moment.unix(value).format('DD.MM.YYYY HH:mm')
    if (date === 'Invalid date') {
      return 'No date available'
    }
    return date
  }

  /**
   * Format millisecond timestamp to datetime
   */
  function msTimestampToDateTime(value: number | null | undefined): string {
    if (!value) return ''
    const date = moment(value).format('DD.MM.YYYY HH:mm')
    if (date === 'Invalid date') {
      return 'No date available'
    }
    return date
  }

  /**
   * Format Unix timestamp to date only
   */
  function timestampToDate(value: number | null | undefined): string {
    if (!value) return ''
    return moment.unix(value).format('DD.MM.YYYY')
  }

  /**
   * Format millisecond timestamp to date only
   */
  function msTimestampToDate(value: number | null | undefined): string {
    if (!value) return ''
    return moment(value).format('DD.MM.YYYY')
  }

  /**
   * Format for datetime picker
   */
  function dateTimePicker(value: string | Date | number | null | undefined): string {
    if (!value) return ''
    return moment(value).format('DD.MM.YYYY HH:mm')
  }

  /**
   * Format boolean as Yes/No
   */
  function yesNo(value: boolean | null | undefined): string {
    if (value) {
      return t('booleans.yes')
    }
    return t('booleans.no')
  }

  /**
   * Format gender value
   */
  function gender(value: string | null | undefined): string {
    if (value === 'male') {
      return t('models.user.attributes.gender.options.male')
    } else if (value === 'female') {
      return t('models.user.attributes.gender.options.female')
    }
    return value || ''
  }

  /**
   * Get status color class
   */
  function statusFilter(status: string): string {
    const statusMap: Record<string, string> = {
      published: 'success',
      draft: 'info'
    }
    return statusMap[status] || ''
  }

  /**
   * Pretty print JSON
   */
  function prettyJson(value: unknown): string {
    return JSON.stringify(value, null, 2)
  }

  /**
   * Sort array
   */
  function sortArray<T>(array: T[]): T[] {
    return [...array].sort()
  }

  return {
    timeAgo,
    numberFormatter,
    toThousandFilter,
    uppercaseFirst,
    decimalComma,
    formatDate,
    timestampToDateTime,
    msTimestampToDateTime,
    timestampToDate,
    msTimestampToDate,
    dateTimePicker,
    yesNo,
    gender,
    statusFilter,
    prettyJson,
    sortArray,
    // Aliases for backwards compatibility
    date: formatDate
  }
}

// Also export standalone functions for use outside of setup context
export const formatters = {
  timeAgo(time: number): string {
    const between = Date.now() / 1000 - Number(time)
    if (between < 3600) {
      return ~~(between / 60) + ' minute' + (~~(between / 60) === 1 ? '' : 's')
    } else if (between < 86400) {
      return ~~(between / 3600) + ' hour' + (~~(between / 3600) === 1 ? '' : 's')
    } else {
      return ~~(between / 86400) + ' day' + (~~(between / 86400) === 1 ? '' : 's')
    }
  },

  formatDate(value: string | Date | number | null | undefined): string {
    if (!value) return ''
    return moment(value).format('DD.MM.YYYY')
  },

  timestampToDateTime(value: number | null | undefined): string {
    if (!value) return ''
    const date = moment.unix(value).format('DD.MM.YYYY HH:mm')
    return date === 'Invalid date' ? 'No date available' : date
  },

  msTimestampToDateTime(value: number | null | undefined): string {
    if (!value) return ''
    const date = moment(value).format('DD.MM.YYYY HH:mm')
    return date === 'Invalid date' ? 'No date available' : date
  },

  timestampToDate(value: number | null | undefined): string {
    if (!value) return ''
    return moment.unix(value).format('DD.MM.YYYY')
  },

  msTimestampToDate(value: number | null | undefined): string {
    if (!value) return ''
    return moment(value).format('DD.MM.YYYY')
  }
}
